import React, { useState } from 'react';
import { flag } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Home } from 'lucide-react';
import DashboardMobileDrawer from '../dashboard/DashboardMobileDrawer';
import DashboardMobileNav from '../dashboard/DashboardMobileNav';

/**
 * Shared header component for all Venue Partner dashboard pages
 *
 * Props:
 * - user: current user object
 * - venuePartner: venue partner data
 * - locationData: location data (for coaster inventory, etc)
 * - minimal: logo + badge only. Used while an application is pending or rejected,
 *   when the partner has no hub yet and every nav target would fetch against a hub
 *   that does not exist and land on an empty or error state.
 */
export default function VenueHeader({ user, venuePartner, locationData, minimal = false }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const routeLocation = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Navigation links. SLOT_MODEL (Phase 8B): only /venue/advertisers still uses this legacy
  // header, so its links match the slot venue nav (DashboardNavLinks): Deliveries instead of
  // Coasters and no Analytics (both of those routes redirect with the flag on).
  const navLinks = flag('SLOT_MODEL')
    ? [
      { label: 'Dashboard', path: '/venue/dashboard' },
      { label: 'Deliveries', path: '/venue/deliveries' },
      { label: 'Advertisers', path: '/venue/advertisers' },
      { label: 'Settings', path: '/venue/settings' },
    ]
    : [
      { label: 'Dashboard', path: '/venue/dashboard' },
      { label: 'Coasters', path: '/venue/coasters' },
      ...(flag('VENUE_ANALYTICS') ? [{ label: 'Analytics', path: '/venue/analytics' }] : []),
      { label: 'Advertisers', path: '/venue/advertisers' },
      { label: 'Settings', path: '/venue/settings' },
    ];
  
  const isActive = (path) => routeLocation.pathname === path;
  
  // Prepare badges for mobile nav
  const badges = {
    coasterInventory: flag('COASTER_INVENTORY') ? (locationData?.coaster_inventory || null) : null,
    advertiserCount: locationData?.advertiser_count || null,
  };
  
  // Prepare venue data for mobile nav
  const venueData = venuePartner ? {
    venue_name: venuePartner.venue_name,
    venue_type: venuePartner.venue_type,
    city: venuePartner.city || locationData?.city,
  } : null;

  return (
    <>
      <div 
        className="fixed top-0 left-0 right-0 z-50"
        style={{ 
          backgroundColor: '#1e1b4b',
          boxShadow: '0 4px 20px rgba(30, 27, 75, 0.3)'
        }}
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          {/* Left: Logo + Badge */}
          <div className="flex items-center gap-3">
            <img src="/tappd-logo-white.png" alt="Tappd" className="h-7 md:h-8" />
            <span 
              className="px-2.5 py-1 text-[10px] md:text-[11px] font-bold uppercase text-white"
              style={{ backgroundColor: '#14b8a6', borderRadius: '9999px' }}
            >
              Venue
            </span>
          </div>
          
          {/* Center: Desktop Nav Links */}
          {!minimal && (
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className="px-4 py-2 text-[14px] font-medium transition-all"
                style={{
                  borderRadius: '8px',
                  color: isActive(link.path) ? 'white' : 'rgba(255, 255, 255, 0.6)',
                  backgroundColor: isActive(link.path) ? '#14b8a6' : 'transparent',
                  boxShadow: isActive(link.path) ? '0 4px 15px rgba(20, 184, 166, 0.4)' : 'none'
                }}
              >
                {link.label}
              </button>
            ))}
          </nav>
          )}
          
          {/* Right: Desktop Home + Mobile Hamburger */}
          <div className="flex items-center gap-2">
            {/* Desktop: Log out */}
            <button
              type="button"
              onClick={async () => { try { await logout(); } finally { window.location.href = '/'; } }}
              className={`${minimal ? 'flex' : 'hidden md:flex'} px-3 h-10 items-center justify-center text-white/60 hover:text-white text-sm`}
              style={{ borderRadius: '8px' }}
            >
              Log out
            </button>
            {/* Desktop: Home button */}
            <button
              aria-label="Go to homepage"
              onClick={() => navigate('/')}
              className="hidden md:flex w-10 h-10 items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
              style={{ borderRadius: '8px' }}
            >
              <Home size={20} aria-hidden="true" />
            </button>
            
            {/* Mobile: Hamburger */}
            {!minimal && (
            <button
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
              style={{ borderRadius: '8px' }}
            >
              <Menu size={24} aria-hidden="true" />
            </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Mobile Drawer */}
      {!minimal && (
      <DashboardMobileDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        variant="venue"
      >
        <DashboardMobileNav
          variant="venue"
          user={user}
          onClose={() => setMobileMenuOpen(false)}
          venueData={venueData}
          badges={badges}
        />
      </DashboardMobileDrawer>
      )}
    </>
  );
}
